// Obere Ribbon-Leiste des Dacheditors (pvXpert-Stil): runde Icon-Buttons.
import type { ReactNode } from "react";
import {
  IconCheck,
  IconImport,
  IconReset,
  IconTrash,
  IconImage,
  IconEye,
  IconCube,
  IconPin,
  IconClose,
} from "./icons.js";

interface RibbonProps {
  roofCount: number;
  totalAreaM2: number;
  projectLabel: string;
  showBackground: boolean;
  showBuilding: boolean;
  show3d: boolean;
  saving: boolean;
  onSave: () => void;
  onImport: () => void;
  onReset: () => void;
  onDelete: () => void;
  onToggleBackground: () => void;
  onToggleBuilding: () => void;
  onToggle3d: () => void;
  onSnapLocation: () => void;
  onClose: () => void;
}

const RIBBON_GRADIENT =
  "linear-gradient(110deg,#2766cf 0%,#1f59bd 46%,#3f86e6 100%)";

function RibbonButton({
  icon,
  label,
  active,
  danger,
  onClick,
  title,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
  title?: string;
}) {
  const offBorder = danger ? "rgba(254,202,202,0.7)" : "rgba(255,255,255,0.62)";
  const offColor = danger ? "#fecaca" : "#fff";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        minWidth: 56,
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
      }}
    >
      <span
        style={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.12s",
          border: `1.5px solid ${active ? "#fff" : offBorder}`,
          background: active ? "#fff" : "rgba(255,255,255,0.06)",
          color: active ? "#1f59bd" : offColor,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 600,
          color: "#fff",
          letterSpacing: "0.01em",
          textAlign: "center",
          lineHeight: 1.15,
        }}
      >
        {label}
      </span>
    </button>
  );
}

export function RoofRibbon(props: RibbonProps) {
  const divider = (
    <span
      style={{
        width: 1,
        height: 46,
        background: "rgba(255,255,255,0.22)",
        margin: "0 6px",
        flexShrink: 0,
      }}
    />
  );

  return (
    <div
      style={{
        height: 78,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "0 8px 0 14px",
        background: RIBBON_GRADIENT,
        boxShadow: "0 1px 0 rgba(0,0,0,0.15)",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1, marginRight: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
          Energy<span style={{ color: "#7ef0a6" }}>OS</span>
        </span>
        <span
          style={{
            fontSize: 9.5,
            color: "rgba(255,255,255,0.7)",
            marginTop: 3,
            letterSpacing: "0.04em",
          }}
        >
          DACHEDITOR
        </span>
      </div>
      {divider}

      <RibbonButton icon={<IconCheck />} label="Speichern" onClick={props.onSave} title="Speichern" />
      <RibbonButton icon={<IconImport />} label="Einpassen" onClick={props.onImport} title="Auf alle Flächen einpassen" />
      <RibbonButton icon={<IconReset />} label="Zurücksetzen" onClick={props.onReset} title="Zeichnung/Auswahl zurücksetzen" />
      <RibbonButton icon={<IconTrash />} label="Löschen" danger onClick={props.onDelete} title="Ausgewähltes Objekt löschen" />
      {divider}
      <RibbonButton icon={<IconImage />} label="Hintergrund" active={props.showBackground} onClick={props.onToggleBackground} />
      <RibbonButton icon={<IconEye />} label="Gebäude" active={props.showBuilding} onClick={props.onToggleBuilding} title="Gebäude-Grundriss zeigen" />
      <RibbonButton icon={<IconCube />} label="3D" active={props.show3d} onClick={props.onToggle3d} title="3D-Ansicht (Bearbeitung pausiert)" />
      <RibbonButton icon={<IconPin />} label="An Standort" onClick={props.onSnapLocation} title="Objekte auf Projektkoordinate verschieben" />

      {/* Rechts: Zusammenfassung + Close */}
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginRight: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#fff" }}>
          {props.roofCount} Dachflächen · {props.totalAreaM2.toFixed(1)} m²
        </span>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
          {props.projectLabel}
        </span>
      </div>
      <button
        type="button"
        onClick={props.onClose}
        title="Editor schließen"
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          border: "1.5px solid rgba(255,255,255,0.6)",
          background: "transparent",
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconClose size={16} />
      </button>
      {props.saving && (
        <span style={{ position: "absolute", left: 14, bottom: 2, fontSize: 9, color: "rgba(255,255,255,0.7)" }}>
          speichert…
        </span>
      )}
    </div>
  );
}
